import { React, useContext, useState } from "react";
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { AuthContext } from '../utils/AuthenticationCheck';
import { Shield, ShieldOff, ShieldCheck, Copy, CheckCircle } from 'lucide-react';
import '../style/Mfa.css';

export default function MfaSettings() {
  const { currentUser, setCurrentUser } = useContext(GlobalStateContext);
  const { user } = useContext(AuthContext);
  const BASE_URL = process.env.REACT_APP_API_URL;

  // 'idle' | 'qr' | 'confirm' | 'disabling' | 'done_enabled' | 'done_disabled'
  const [step, setStep] = useState('idle');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const mfaEnabled = currentUser?.mfa_enabled ?? false;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
  });

  const handleStartEnable = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/mfa/setup`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ uid: user?.uid, email: user?.email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Setup failed'); return; }
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('qr');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/mfa/confirm-setup`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ uid: user?.uid, email: user?.email, code }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Confirmation failed.'); return; }
      setCurrentUser({ ...currentUser, mfa_enabled: true });
      setStep('done_enabled');
      setCode('');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Enter your current 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/mfa/disable`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ uid: user?.uid, email: user?.email, code }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Disable failed.'); return; }
      setCurrentUser({ ...currentUser, mfa_enabled: false });
      setStep('done_disabled');
      setCode('');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => { setStep('idle'); setCode(''); setError(''); setQrCode(''); setSecret(''); };

  // ─── IDLE: status card ────────────────────────────────────────────────────
  if (step === 'idle') {
    return (
      <div className="mfa-settings-card">
        <div className="mfa-settings-header">
          {mfaEnabled
            ? <ShieldCheck className="mfa-status-icon mfa-status-on" size={32} />
            : <ShieldOff className="mfa-status-icon mfa-status-off" size={32} />}
          <div>
            <h3 className="mfa-settings-title">Two-Factor Authentication</h3>
            <p className="mfa-settings-subtitle">
              {mfaEnabled
                ? 'MFA is enabled. Your account requires a code at every sign-in.'
                : 'MFA is disabled. Add an extra layer of security to your account.'}
            </p>
          </div>
        </div>

        {error && <p className="mfa-error">{error}</p>}

        {mfaEnabled ? (
          <button className="mfa-btn mfa-btn-danger" onClick={() => { setStep('disabling'); setError(''); }}>
            <ShieldOff size={16} /> Disable MFA
          </button>
        ) : (
          <button className="mfa-btn mfa-btn-primary" onClick={handleStartEnable} disabled={loading}>
            <Shield size={16} /> {loading ? 'Loading…' : 'Enable MFA'}
          </button>
        )}
      </div>
    );
  }

  // ─── QR: show QR code and manual key ─────────────────────────────────────
  if (step === 'qr') {
    return (
      <div className="mfa-settings-card">
        <h3 className="mfa-settings-title">Scan QR Code</h3>
        <p className="mfa-settings-subtitle">
          Open your authenticator app (Google Authenticator, Authy, etc.) and scan this QR code.
        </p>

        <div className="mfa-qr-wrap">
          <img src={qrCode} alt="MFA QR Code" className="mfa-qr-img" />
        </div>

        <p className="mfa-manual-label">Or enter this key manually:</p>
        <div className="mfa-secret-row">
          <code className="mfa-secret-code">{secret}</code>
          <button type="button" className="mfa-copy-btn" onClick={copySecret}>
            {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="mfa-btn-row">
          <button className="mfa-btn mfa-btn-ghost" onClick={reset}>Cancel</button>
          <button className="mfa-btn mfa-btn-primary" onClick={() => { setStep('confirm'); setError(''); }}>
            Next: Enter Code
          </button>
        </div>
      </div>
    );
  }

  // ─── CONFIRM: verify first code ───────────────────────────────────────────
  if (step === 'confirm') {
    return (
      <div className="mfa-settings-card">
        <h3 className="mfa-settings-title">Confirm Setup</h3>
        <p className="mfa-settings-subtitle">
          Enter the 6-digit code your authenticator app is showing now to confirm setup.
        </p>

        <form onSubmit={handleConfirm} className="mfa-inline-form">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            className="mfa-code-input mfa-code-input-sm"
            autoFocus
          />
          {error && <p className="mfa-error">{error}</p>}
          <div className="mfa-btn-row">
            <button type="button" className="mfa-btn mfa-btn-ghost" onClick={() => setStep('qr')}>Back</button>
            <button type="submit" className="mfa-btn mfa-btn-primary" disabled={loading || code.length !== 6}>
              {loading ? 'Confirming…' : 'Enable MFA'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── DISABLING: require current TOTP code ────────────────────────────────
  if (step === 'disabling') {
    return (
      <div className="mfa-settings-card">
        <h3 className="mfa-settings-title">Disable MFA</h3>
        <p className="mfa-settings-subtitle">
          Enter your current authenticator code to confirm you want to disable MFA.
        </p>

        <form onSubmit={handleDisable} className="mfa-inline-form">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            className="mfa-code-input mfa-code-input-sm"
            autoFocus
          />
          {error && <p className="mfa-error">{error}</p>}
          <div className="mfa-btn-row">
            <button type="button" className="mfa-btn mfa-btn-ghost" onClick={reset}>Cancel</button>
            <button type="submit" className="mfa-btn mfa-btn-danger" disabled={loading || code.length !== 6}>
              {loading ? 'Disabling…' : 'Confirm Disable'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── DONE states ──────────────────────────────────────────────────────────
  if (step === 'done_enabled' || step === 'done_disabled') {
    const enabled = step === 'done_enabled';
    return (
      <div className="mfa-settings-card mfa-done-card">
        <CheckCircle size={40} className={enabled ? 'mfa-status-on' : 'mfa-status-off'} />
        <h3 className="mfa-settings-title">
          {enabled ? 'MFA Enabled' : 'MFA Disabled'}
        </h3>
        <p className="mfa-settings-subtitle">
          {enabled
            ? 'Your account is now protected with two-factor authentication.'
            : 'Two-factor authentication has been removed from your account.'}
        </p>
        <button className="mfa-btn mfa-btn-primary" onClick={reset}>Done</button>
      </div>
    );
  }

  return null;
}
